import { supabase } from '../lib/supabase';

const BOOTH_IMAGES_BUCKET = 'booth-images';

/**
 * Two-step institute lookup: name on institute_profiles, city on users
 */
async function findInstituteByNameAndCity(name, city) {
  const { data: institutes, error: instError } = await supabase
    ?.from('institute_profiles')
    ?.select('id, institute_name, user_id')
    ?.ilike('institute_name', name?.trim())
    ?.limit(50);
  if (instError) throw instError;
  if (!institutes || institutes?.length === 0) return null;

  const userIds = institutes?.map((i) => i?.user_id)?.filter(Boolean);
  if (userIds?.length === 0) return null;

  const { data: users, error: userError } = await supabase
    ?.from('users')
    ?.select('id, city')
    ?.in('id', userIds);
  if (userError) throw userError;

  const cityLower = city?.trim()?.toLowerCase();
  const matchedUser = users?.find((u) => u?.city?.toLowerCase() === cityLower);
  if (!matchedUser) return null;

  return institutes?.find((i) => i?.user_id === matchedUser?.id) || null;
}

/**
 * Parse filename to extract institute name and city.
 * Expected format: "InstituteName_City.ext" * e.g."Acharya Business School_Bangalore.png"
 */
function parseLogoFilename(filename) {
  // Remove extension
  const nameWithoutExt = filename?.replace(/\.[^/.]+$/, '');
  // Split on last underscore to get city
  const lastUnderscoreIdx = nameWithoutExt?.lastIndexOf('_');
  if (lastUnderscoreIdx === -1) return null;
  const instituteName = nameWithoutExt?.substring(0, lastUnderscoreIdx)?.trim();
  const city = nameWithoutExt?.substring(lastUnderscoreIdx + 1)?.trim();
  if (!instituteName || !city) return null;
  return { instituteName, city };
}

/**
 * Upload a single logo file to Supabase storage and update institute_profiles.
 */
async function uploadLogoForInstitute(instituteId, file) {
  const timestamp = Date.now();
  const fileExt = file?.name?.split('.')?.pop()?.toLowerCase();
  const fileName = `logo-${timestamp}.${fileExt}`;
  const filePath = `${instituteId}/${fileName}`;

  const { data, error: uploadError } = await supabase?.storage
    ?.from(BOOTH_IMAGES_BUCKET)
    ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase?.storage
    ?.from(BOOTH_IMAGES_BUCKET)
    ?.getPublicUrl(data?.path || filePath);

  const publicUrl = urlData?.publicUrl;

  const { error: updateError } = await supabase
    ?.from('institute_profiles')
    ?.update({ logo_url: publicUrl })
    ?.eq('id', instituteId);

  if (updateError) throw updateError;

  return publicUrl;
}

/**
 * Main bulk logo upload function.
 * Accepts an array of File objects (images) or a ZIP file.
 * Each image filename must follow: "InstituteName_City.ext"
 *
 * @param {File[]} imageFiles - Array of image files
 * @param {{ onProgress: Function }} options
 */
export async function bulkLogoUpload(imageFiles, { onProgress } = {}) {
  const VALID_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const results = [];
  let totalRowsUploaded = 0;
  let totalRowsFailed = 0;
  let totalRowsSkipped = 0;
  const totalRows = imageFiles?.length;

  onProgress?.({ phase: 'uploading', rowIndex: 0, totalRows });

  for (let i = 0; i < imageFiles?.length; i++) {
    const file = imageFiles?.[i];
    const filename = file?.name;
    const ext = filename?.split('.')?.pop()?.toLowerCase();

    onProgress?.({ phase: 'uploading', rowIndex: i + 1, totalRows, filename });

    if (!VALID_EXTS?.includes(ext)) {
      results?.push({
        filename,
        status: 'failed',
        error: `Unsupported file type ".${ext}". Use: ${VALID_EXTS?.join(', ')}`,
        rowNumber: i + 1,
      });
      totalRowsFailed++;
      continue;
    }

    const parsed = parseLogoFilename(filename);
    if (!parsed) {
      results?.push({
        filename,
        status: 'failed',
        error: 'Filename must follow format: InstituteName_City.ext (e.g. "Acharya Business School_Bangalore.png")',
        rowNumber: i + 1,
      });
      totalRowsFailed++;
      continue;
    }

    const { instituteName, city } = parsed;

    try {
      const institute = await findInstituteByNameAndCity(instituteName, city);
      if (!institute) {
        results?.push({
          filename,
          instituteName,
          city,
          status: 'not_found',
          error: `No institute found matching "${instituteName}" in "${city}"`,
          rowNumber: i + 1,
        });
        totalRowsSkipped++;
        continue;
      }

      const logoUrl = await uploadLogoForInstitute(institute?.id, file);
      results?.push({
        filename,
        instituteName,
        city,
        status: 'success',
        logoUrl,
        rowNumber: i + 1,
      });
      totalRowsUploaded++;
    } catch (err) {
      results?.push({
        filename,
        instituteName,
        city,
        status: 'failed',
        error: err?.message || 'Upload failed',
        rowNumber: i + 1,
      });
      totalRowsFailed++;
    }
  }

  return {
    results,
    summary: { totalRows, totalRowsUploaded, totalRowsFailed, totalRowsSkipped },
  };
}
